import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useVoiceTranscription from "../../hooks/useVoiceTranscription";
import {
  setTranscriptionState, 
  clearTranscription 
} from "../../store/TranscriptionSlice/TranscriptionSlice";
import { toggleLivekitTranscription } from "../../store/livekitSlice/livekitSlice";
import TranscriptionDisplay from "./TranscriptionDisplay/TranscriptionDisplay";
import StatusBar from "../../../ScreenRecording/components/StatusBar";
import { getTenantSettings } from "../../../Auth/store/authThunks";
import { getSubDomain, tenantHasFeature } from "../../../Auth/utils/AuthUtils";
import { getTransciptionService } from "../../Transcription/TranscriptionService";

const DEFAULT_DISPLAY_LANG = "fr";

const VoiceTranscription = ({ room }) => {
  const dispatch = useDispatch();
  const { localAvatar, tenantSettings } = useSelector((state) => state.auth);
  const { livekitMicActive } = useSelector((state) => state.livekit);
  const tenantRefreshTimer = useRef(null);
  const previousSttModelRef = useRef(null);
  const [languageSyncNotice, setLanguageSyncNotice] = useState("");

  // Réunion souvent dans un autre onglet que l'admin : au retour sur la page, resynchroniser le moteur STT affiché.
  useEffect(() => {
    let subDomain;
    try {
      subDomain = getSubDomain();
    } catch {
      return undefined;
    }
    if (!subDomain) return undefined;

    const scheduleRefresh = () => {
      if (document.visibilityState !== "visible") return;
      if (tenantRefreshTimer.current) clearTimeout(tenantRefreshTimer.current);
      tenantRefreshTimer.current = setTimeout(() => {
        tenantRefreshTimer.current = null;
        dispatch(getTenantSettings({ params: [{ subDomain }] }));
      }, 40000);
    };

    document.addEventListener("visibilitychange", scheduleRefresh);
    window.addEventListener("focus", scheduleRefresh);
    return () => {
      if (tenantRefreshTimer.current) clearTimeout(tenantRefreshTimer.current);
      document.removeEventListener("visibilitychange", scheduleRefresh);
      window.removeEventListener("focus", scheduleRefresh);
    };
  }, [dispatch]);
  const recorder = useSelector(
    (state) => state.recorder
);
  // Helper URL
  const rawSttModel = tenantSettings?.stt_model || "voxtral";
  const sttModel = (
    rawSttModel.startsWith("ws://")
    || rawSttModel.startsWith("wss://")
    || ["voxtral", "kyutai", "whisper", "canary"].includes(rawSttModel)
  )
    ? rawSttModel
    : "voxtral";

  const getApiUrl = () => {
    if (sttModel.startsWith("ws://") || sttModel.startsWith("wss://")) {
      return sttModel;
    }
    return `wss://stt.aifel.eu:8089/${sttModel}`;
  };

  const TSUrl = getApiUrl();

  // TEMP QA — retirer après validation : voir quel preset / URL ce client utilise (sans onglet Network).
  const sttDebugLine =
    sttModel.startsWith("ws://") || sttModel.startsWith("wss://")
      ? `STT [test] perso (${sttModel.length > 42 ? `${sttModel.slice(0, 42)}…` : sttModel})`
      : `STT [test] moteur : ${sttModel}`;

  const {
    TSstatus,
    isOwner,
    transcription,
    translation,
    detectedLang,
    targetLang,
    toggleTranscription,
    TSNotif,
    TSChecking,
    owner,
    sttLanguageOptions,
    translationLanguageOptions,
    sttSourceLang,
    translationTargetLang,
  } = useVoiceTranscription(
    TSUrl,
    room,
    localAvatar?.name,
    localAvatar?.email,
    livekitMicActive,
    true
  );

  useEffect(() => {
    dispatch(toggleLivekitTranscription(TSstatus === "active"));
  }, [TSstatus, dispatch]);

  useEffect(() => {
    dispatch(
      setTranscriptionState({
        TSstatus,
        isOwner,
        transcription,
        owner,
        TSNotif,
        TSChecking,
      })
    );
  }, [TSstatus, isOwner, transcription, owner, TSNotif, TSChecking, dispatch]);

  const handleLanguagesChange = (nextStt) => {
    dispatch(setTranscriptionState({ sttSourceLang: nextStt || "" }));
    getTransciptionService()?.emitTranscriptionLang(nextStt, null);
  };

  const handleDisplayLanguageChange = (nextTargetLang) => {
    setLanguageSyncNotice("");
    if (translationTargetLang !== nextTargetLang) {
      dispatch(
        setTranscriptionState({
          translationTargetLang: nextTargetLang || "",
          translation: "",
          detectedLang: null,
          targetLang: nextTargetLang || "",
        })
      );
    }
    getTransciptionService()?.emitTranscriptionLang(null, nextTargetLang);
  };

  const handleRequestSttLanguages = () => {
    getTransciptionService()?.requestSttLanguages?.();
  };

  useEffect(() => {
    if (previousSttModelRef.current === null) {
      previousSttModelRef.current = sttModel;
      return undefined;
    }

    if (previousSttModelRef.current === sttModel) {
      return undefined;
    }

    previousSttModelRef.current = sttModel;
    setLanguageSyncNotice("Langue remise en français");
    dispatch(
      setTranscriptionState({
        translationTargetLang: DEFAULT_DISPLAY_LANG,
        translation: "",
        detectedLang: null,
        targetLang: DEFAULT_DISPLAY_LANG,
      })
    );

    const syncDisplayLang = () => {
      getTransciptionService()?.emitTranscriptionLang(null, DEFAULT_DISPLAY_LANG);
    };
    const syncTimers = [
      setTimeout(syncDisplayLang, 300),
      setTimeout(syncDisplayLang, 1200),
      setTimeout(() => setLanguageSyncNotice(""), 2600),
    ];

    return () => {
      syncTimers.forEach(clearTimeout);
    };
  }, [sttModel, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearTranscription());
    };
  }, [dispatch]);

  return (
    <>
      {(tenantHasFeature("RECORD_SYSTEM_ENABLED") || tenantHasFeature("TRANSCRIPTION_ENABLED")) && ((TSstatus === "active" || recorder.status === "recording" )) && (

        <div>
          {console.log("DEBUG STATUS BAR ", recorder.status === "recording")}
        <StatusBar
          transcribed_text={transcription}
          translated_text={translation}
          detected_lang={detectedLang}
          target_lang={targetLang}
          stt_language_options={sttLanguageOptions}
          stt_source_lang={sttSourceLang}
          on_languages_change={handleLanguagesChange}
          display_language_options={translationLanguageOptions}
          display_lang={translationTargetLang}
          on_display_language_change={handleDisplayLanguageChange}
          on_request_stt_languages={handleRequestSttLanguages}
          language_sync_notice={languageSyncNotice}
          owner={typeof owner === "string" ? owner : owner?.name || "Inconnu"}
          TSstatus={TSstatus}
          isOwner={isOwner}
          toggleTranscription={toggleTranscription}
          sttDebugLine={sttDebugLine}
          sttDebugTitle={TSUrl}
        />
        </div>

        
      )}
    </>
  );
};

export default VoiceTranscription;
